// CollapsibleBase.tsx
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Collapse } from '../Collapse/Collapse';
import { twMerge } from '@shared/utils/twMerge';

export type CollapsibleBaseClassNames = {
  outer?: string;
  container?: string;
  header?: string;
  content?: string; // animated shell (max-height)
  body?: string; // inner body wrapper (we animate opacity/transform here)
  icon?: string;
  iconBtn?: string;
};

export type CollapsibleBaseProps = {
  label: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onToggle?: (open: boolean) => void;
  iconPosition?: 'left' | 'right';
  resize?: number | string;
  classNames?: CollapsibleBaseClassNames;
  motion?: {
    heightDurationMs?: number; // default 700
    heightEasing?: string; // default cubic-bezier(0.16, 1, 0.3, 1) (easeOutExpo-ish)
    bodyDurationMs?: number; // default 420
    bodyEasing?: string; // default cubic-bezier(0.2, 0, 0, 1)
    bodyOffset?: number; // default 6 (px)
    bodyDelayMs?: number; // default 120
    softenPadding?: boolean; // default false (animate body vertical padding)
  };
};

export const CollapsibleBase: FC<CollapsibleBaseProps> = ({
  label,
  children,
  open,
  defaultOpen = false,
  onToggle,
  iconPosition = 'left',
  resize,
  classNames = {},
  motion,
}) => {
  const isControlled = typeof open === 'boolean';
  const [internalOpen, setInternalOpen] = useState<boolean>(defaultOpen);
  const effectiveOpen = isControlled ? (open as boolean) : internalOpen;

  // Tuned motion defaults
  const heightDuration = motion?.heightDurationMs ?? 700;
  const heightEasing = motion?.heightEasing ?? 'cubic-bezier(0.16,1,0.3,1)';
  const bodyDuration = motion?.bodyDurationMs ?? 420;
  const bodyEasing = motion?.bodyEasing ?? 'cubic-bezier(0.2,0,0,1)';
  const bodyOffsetPx = motion?.bodyOffset ?? 6;
  const bodyDelay = motion?.bodyDelayMs ?? 120;
  const softenPadding = motion?.softenPadding ?? false;

  // Re-measure parent height when body changes (nested inners, async content)
  const [resizeTick, setResizeTick] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!bodyRef.current || !('ResizeObserver' in window)) {
      return;
    }
    const ro = new ResizeObserver(() => setResizeTick(t => t + 1));
    ro.observe(bodyRef.current);
    return () => ro.disconnect();
  }, []);

  const combinedResize = useMemo(() => {
    const base = typeof resize === 'number' || typeof resize === 'string' ? String(resize) : '';
    return `${base}:${resizeTick}`;
  }, [resize, resizeTick]);

  const handleToggle = () => {
    const next = !effectiveOpen;
    if (!isControlled) {
      setInternalOpen(next);
    }
    onToggle?.(next);
  };

  const isLeft = iconPosition === 'left';

  return (
    <div className={twMerge(classNames.outer)}>
      <Collapse
        open={effectiveOpen}
        notUseInnerState
        onClick={handleToggle}
        resize={combinedResize}
        label={<div className="flex w-full items-center gap-2">{label}</div>}
        styles={{
          container: twMerge(classNames.container),
          header: twMerge(
            isLeft ? 'relative pl-7 pr-3' : 'pr-3',
            'flex items-center gap-2',
            classNames.header
          ),
          // Smoother HEIGHT tween
          content: twMerge(
            `overflow-hidden will-change-[max-height] transition-[max-height] duration-[${heightDuration}ms] ease-[${heightEasing}]`,
            classNames.content
          ),
          iconBtn: twMerge(
            isLeft
              ? 'absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-ghostWhite focus:outline-none focus:ring-0'
              : 'w-3 h-3 rounded bg-ghostWhite focus:outline-none focus:ring-0',
            classNames.iconBtn
          ),
          icon: twMerge('w-2 h-2', classNames.icon),
        }}
      >
        {/* Fade/slide the inner body; optionally ease its vertical padding too */}
        <div
          ref={bodyRef}
          className={twMerge(
            // opacity/transform
            `transition-opacity transition-transform duration-[${bodyDuration}ms] ease-[${bodyEasing}]`,
            effectiveOpen
              ? `translate-y-0 opacity-100 delay-[${bodyDelay}ms]`
              : `opacity-0 -translate-y-[${bodyOffsetPx}px] delay-0`,
            // optional padding tween for extra smoothness
            softenPadding
              ? `transition-[padding] duration-[${bodyDuration}ms] ease-[${bodyEasing}] ${effectiveOpen ? 'py-3' : 'py-0'}`
              : '',
            classNames.body
          )}
        >
          {children}
        </div>
      </Collapse>
    </div>
  );
};
